import { parseCurrency, parsePercent } from '../../../app/currency';

export function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const fromCurrency = parseCurrency(value);
  if (fromCurrency !== '') {
    return parseFloat(fromCurrency) || 0;
  }
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

export function getStoreSharePercentFromItem(item) {
  const sale = toNumber(item.sale_price);
  const financials = computeConsignmentFinancials(item);
  if (sale <= 0) {
    return toNumber(item.commission_percent);
  }
  return (financials.storeShare / sale) * 100;
}

export function computeConsignmentFinancials(input) {
  const sale = toNumber(input.sale_price);
  const mode = input.negotiation_mode || 'FIXED_PRICE';

  if (input.store_share_amount != null && input.store_share_amount !== '') {
    const storeShare = toNumber(input.store_share_amount);
    return { sale, storeShare, net: Math.max(0, sale - storeShare) };
  }

  if (mode === 'PERCENT_COMMISSION') {
    const pct = toNumber(input.commission_percent);
    const storeShare = sale * (pct / 100);
    return { sale, storeShare, net: Math.max(0, sale - storeShare) };
  }

  if (mode === 'FIXED_VALUE_TO_STORE') {
    const storeShare = toNumber(input.fixed_value_to_store);
    return { sale, storeShare, net: Math.max(0, sale - storeShare) };
  }

  if (mode === 'FIXED_MARGIN') {
    const companyPct = toNumber(input.fixed_margin_percent);
    const net = sale * (companyPct / 100);
    return { sale, storeShare: Math.max(0, sale - net), net };
  }

  const net = toNumber(input.net_value);
  return { sale, storeShare: Math.max(0, sale - net), net };
}

export function buildStoreShareFromPercent(sale, percent) {
  if (!Number.isFinite(sale) || sale <= 0 || !Number.isFinite(percent)) {
    return 0;
  }
  return sale * (percent / 100);
}

export function buildStoreSharePercentFromAmount(sale, amount) {
  if (!Number.isFinite(sale) || sale <= 0 || !Number.isFinite(amount)) {
    return 0;
  }
  return (amount / sale) * 100;
}

export function normalizeNegotiationMode(mode) {
  if (mode === 'FREE_PRICE') {
    return 'FREE_PRICE';
  }
  return 'FIXED_PRICE';
}

export function isFixedNetCompanyMode(mode) {
  return normalizeNegotiationMode(mode) === 'FREE_PRICE';
}

export function buildItemTermsPayload(mode, sale, storeShare, storePercent, fixedNet = 0) {
  const normalized = normalizeNegotiationMode(mode);
  if (normalized === 'FREE_PRICE') {
    const net = Math.max(0, fixedNet);
    return {
      sale_price: 0,
      negotiation_mode: 'FREE_PRICE',
      commission_percent: 0,
      fixed_value_to_store: 0,
      fixed_margin_percent: 0,
      net_value: net,
    };
  }

  const net = Math.max(0, sale - storeShare);
  return {
    sale_price: sale,
    negotiation_mode: 'FIXED_PRICE',
    commission_percent: 0,
    fixed_value_to_store: 0,
    fixed_margin_percent: 0,
    net_value: net,
  };
}

export function getSalePriceLabelKey(mode) {
  switch (mode) {
    case 'MIN_PRICE':
      return 'consignment.sale_price_min_label';
    case 'FREE_PRICE':
      return 'consignment.sale_price_free_label';
    case 'FIXED_PRICE':
      return 'consignment.sale_price_fixed_label';
    default:
      return 'consignment.sale_price_default_label';
  }
}

export function getSalePriceHelpKey(mode) {
  switch (mode) {
    case 'MIN_PRICE':
      return 'consignment.sale_price_min_help';
    case 'FREE_PRICE':
      return 'consignment.sale_price_free_help';
    case 'FIXED_PRICE':
      return 'consignment.sale_price_fixed_help';
    default:
      return 'consignment.sale_price_default_help';
  }
}

export function getModeHelpKey(mode) {
  return `consignment.mode_${mode.toLowerCase()}_help`;
}

export function parsePercentNumber(display) {
  const parsed = parsePercent(display);
  return parsed === '' ? 0 : parseFloat(parsed) || 0;
}

export function parseCurrencyNumber(display) {
  const parsed = parseCurrency(display);
  return parsed === '' ? 0 : parseFloat(parsed) || 0;
}
